/* eslint-disable rxjs/finnish */
import './pique-action-panel';
import './pique-action-shareability';
import './pique-action-plan-panel';

import type { BlockSpec } from '@blocksuite/block-std';
import {
  type Cell,
  type Column,
  DatabaseBlockComponent,
  DatabaseBlockSchema,
  DatabaseBlockService,
} from '@blocksuite/blocks';
import { computed, SignalWatcher } from '@lit-labs/preact-signals';
import type { ActionItemsService } from '@pique/core/modules/properties/services/action-items-service';
import { normalizeEmail } from '@pique/core/modules/properties/services/attendee-id-utils';
import { AFFINE_PROPERTIES_ID, readProxyArrayValue, reconcileActionItems, updateActionItemFields } from '@pique/core/modules/properties/services/adapter';
import { ACTION_ITEM_STATUS, ACTION_ITEM_STATUS_VALUES, type ActionItemPriority, ActionItemPrioritySchema, type ActionItemStatus, PageSystemPropertyId, type SerializedAttendee, type SerializedTodoItem, type WorkspaceAffineProperties } from '@pique/core/modules/properties/services/schema';
import { deduplicateAttendees as dedupeAttendees } from '@pique/core/modules/properties/services/utils';
import { customElement, state } from 'lit/decorators.js';
import { html, type TemplateResult } from 'lit/html.js';
import { literal } from 'lit/static-html.js';

import { openActionItemInKanbanSlot } from '../../../components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import { mergeDatabaseRowActionItems } from './action-items-reconciliation';
import type { ActionPlanOption } from './pique-action-plan-panel';


/**
 * BlockSuite Database Block Architecture & Customization
 * =====================================================
 *
 * Core Rendering Pipeline:
 *
 *  +-------------------------+        +-------------------------+
 *  | DatabaseBlockComponent  |<------>| Root Service            |
 *  | (pique-database)        |        | (provides peekView)     |
 *  +-------------------------+        +-------------------------+
 *      |         |
 *      | uses    | configures
 *      v         v
 *  +-------------------------+        +------------------+
 *  | DataSource              |        | DataView         |
 *  | - getPropertyMeta()     |        | (has render())   |
 *  | - addPropertyConfigList |        +------------------+
 *  +-------------------------+                 | renders
 *      |                                       v
 *      | provides                       +------------------+
 *      v                                | DataViewRenderer |
 *  +-------------------------+        | (affine-data-view)|
 *  | ColumnMeta              |        +------------------+
 *  | - model (ColumnModelConfig)             |
 *  | - renderer (ColumnRenderConfig)         | when detail panel opens
 *  |   - cellRenderer                        v
 *  |   - detailRenderer               +------------------+
 *  +-------------------------+        | RecordDetail     |
 *                                     | (affine-data-    |
 *                                     |  view-record)    |
 *                                     +------------------+
 *
 * Key Components & Concepts:
 * 1. DatabaseBlockComponent: The main Lit component for the database block.
 *    We extend this to create 'pique-database'.
 * 2. DataSource: Manages the database's data, schema, and property configurations.
 *    - `addPropertyConfigList`: Provides a list of available `ColumnModelConfig`s,
 *      defining the data structure of column types (e.g., name, default value).
 *      `ColumnRenderConfig` (which specifies UI renderers like `cellRenderer`
 *      and `detailRenderer`).
 * 3. DataView: Responsible for rendering the overall database UI (table, gallery, etc.)
 *    using a `DataViewRenderer`.
 * 4. RecordDetail: The component used for the detail panel when a row is opened.
 * 5. peekViewService: An alternative mechanism for showing modals/detail panels,
 *    used by the `DataViewRenderer` to open the `RecordDetail`.
 *
 */
@customElement('pique-database')
export class PiqueDatabaseBlockComponent extends SignalWatcher(
  DatabaseBlockComponent
) {
  // Computed signal for the current view
  private readonly currentView$ = computed(() => {
    const viewId = this.dataSource.viewManager.currentViewId$.value;
    if (!viewId) return undefined;
    return this.dataSource.viewManager.viewGet(viewId);
  });

  // Keep track of the latest values to avoid duplicate updates
  private readonly cellValueCache = new Map<string, string>();

  // Track which setup is currently running
  private setupInProgress = false;

  // Track per-cell subscriptions for cleanup and to prevent duplication
  private readonly cellSubscriptions = new Map<string, () => void>();

  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private _isCleanedUp = false;

  // Add state for shareability status
  @state()
  accessor isPrivate: boolean = true;

  @state()
  accessor activeServices: string[] = [];

  @state()
  accessor actionPlanOptions: ActionPlanOption[] = [];

  private getActionItemsService(): ActionItemsService | undefined {
    const rootService = this.getRootService() as { actionItemsService?: ActionItemsService } | undefined;
    return rootService?.actionItemsService;
  }

  private normalizeSteps(input: ActionPlanOption[] | ActionPlanOption | null | undefined): ActionPlanOption[] {
    if (!input) {
      return [];
    }
    const stepsArray = Array.isArray(input) ? input : [input];
    const normalized: ActionPlanOption[] = [];
    const seen = new Set<string>();

    for (const step of stepsArray) {
      if (!step) continue;
      const id = String((step as ActionPlanOption).id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      normalized.push({
        id,
        title: typeof step.title === 'string' ? step.title : String(step.title ?? ''),
        description: typeof step.description === 'string' ? step.description : '',
        completed: Boolean(step.completed),
      });
    }

    return normalized;
  }

  /**
   * Handle privacy status change
   */
  private handlePrivacyChanged(e: CustomEvent<{ isPrivate: boolean }>) {
    this.isPrivate = e.detail.isPrivate;
  }

  /**
   * Handle active services change
   */
  private handleServicesChanged(e: CustomEvent<{ activeServices: string[] }>) {
    this.activeServices = e.detail.activeServices;
  }

  /**
   * Set action plan options for the detail view
   */
  public setActionPlanOptions(options: ActionPlanOption[]) {
    this.actionPlanOptions = this.normalizeSteps(options);
  }

  /**
   * Get the title for a specific row
   */
  private getTitleForRow(rowId?: string): string | undefined {
    try {
      const currentView = this.currentView$.value;
      if (!currentView || !rowId) return undefined;

      const titleColumn = currentView.columns$.value.find(colId => {
        const column = currentView.columnGet(colId);
        return column.type === 'title';
      });

      if (!titleColumn) return undefined;

      const cell = currentView.cellGet(rowId, titleColumn);
      const titleValue = cell?.stringValue$?.value;

      return titleValue || undefined;
    } catch (error) {
      console.warn('Error getting title for row:', error);
      return undefined;
    }
  }

  /**
   * Get the description for a specific row
   */
  private getDescriptionForRow(rowId?: string): string | undefined {
    try {
      const currentView = this.currentView$.value;
      if (!currentView || !rowId) return undefined;

      const descColumn = currentView.columns$.value.find(colId => {
        const column = currentView.columnGet(colId);
        return column.name === 'Description' || column.type === 'rich-text';
      });

      if (!descColumn) return undefined;

      const cell = currentView.cellGet(rowId, descColumn);
      const descValue = cell?.stringValue$?.value;

      return descValue || undefined;
    } catch (error) {
      console.warn('Error getting description for row:', error);
      return undefined;
    }
  }

  /**
   * Get transcription data from the page or workspace
   */
  private getTranscriptionData(): Record<string, unknown> | null {
    try {
      // This would need to be implemented based on how transcription data is stored
      // For now, return null as a placeholder
      return null;
    } catch (error) {
      console.warn('Error getting transcription data:', error);
      return null;
    }
  }

  /**
   * Get page content for context
   */
  private getPageContent(): { title: string; id: string; blockCount: number } | null {
    try {
      if (!this.host?.doc) return null;

      // Get basic page information
      return {
        title: this.host.doc.meta?.title || 'Untitled',
        id: this.host.doc.id,
        blockCount: this.host.doc.root?.children?.length || 0
      };
    } catch (error) {
      console.warn('Error getting page content:', error);
      return null;
    }
  }

  /**
   * Build assignedTo attendees from the assignee multi-select column
   */
  private getAssigneesFromRow(
    rowId: string,
    currentView: { cellGet: (rowId: string, columnId: string) => Cell; columnGet: (columnId: string) => Column } | undefined,
    assigneeColId: string,
    preloadedPageAttendees?: SerializedAttendee[]
  ): SerializedTodoItem['assignedTo'] {
    try {
      if (!currentView) return [];
      const cell = currentView.cellGet(rowId, assigneeColId);
      const raw = cell?.value$?.value;
      if (!raw) return [];

      const optionIds: string[] = Array.isArray(raw) ? raw : [raw];
      const column = currentView.columnGet(assigneeColId);
      type MultiSelectOption = { id: string; value: string; color?: string };
      const options = (column.data$?.value as { options?: MultiSelectOption[] } | undefined)?.options ?? [];

      const attendees: SerializedAttendee[] = [];

      // Prefer preloaded attendees if provided; fallback to reading from properties
      let pageAttendees: SerializedAttendee[] = Array.isArray(preloadedPageAttendees) ? preloadedPageAttendees : [];
      if (!pageAttendees.length) {
        try {
          const propertiesProxy = this.host.doc.collection.doc.getMapProxy<'affine:workspace-properties', WorkspaceAffineProperties>('affine:workspace-properties');
          const pageId = this.host.doc.id;
          const pageProps = propertiesProxy.pageProperties?.[pageId];
          const attendeesValue = pageProps?.system?.attendees?.value as SerializedAttendee[] | undefined;
          if (Array.isArray(attendeesValue)) {
            pageAttendees = attendeesValue;
          }
        } catch { }
      }

      optionIds.forEach(id => {
        const opt = options.find(o => o.id === id);
        if (!opt) return;
        const value: string = String(opt.value ?? '');
        // Extract email from option value or id (options were seeded with email-only values)
        const match = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        const email = match ? match[0] : String(id);
        const norm = normalizeEmail(email);
        const fromPage = pageAttendees.find(a => normalizeEmail(a.email || '') === norm);
        if (fromPage) {
          attendees.push({
            id: String(fromPage.id),
            name: fromPage.name || fromPage.email,
            email: fromPage.email,
            avatar: fromPage.avatar,
            present: fromPage.present ?? true,
          });
        }
      });
      return dedupeAttendees(attendees);
    } catch (e) {
      console.warn('Error building assignees from row:', e);
      return [];
    }
  }

  override connectedCallback() {
    super.connectedCallback();

    if (!this.dataSource) {
      return;
    }

    const pageId = this.host?.doc?.id;
    if (pageId && this.model?.isActionItemsDatabase) {
      const service = this.getActionItemsService();
      if (service) {
        service.registerDatabaseBlock(pageId, this);
      }
    }

    this._disposables.add(
      this.dataSource.viewManager.currentViewId$.subscribe(_viewId => {
        this.setupCellTracking();
        this.scheduleReconcile();
      })
    );

    let rowChangeTimer: ReturnType<typeof setTimeout> | null = null;
    this._disposables.add(
      this.dataSource.rows$.subscribe(_rows => {
        if (rowChangeTimer) clearTimeout(rowChangeTimer);
        rowChangeTimer = setTimeout(() => {
          this.setupCellTracking();
          this.scheduleReconcile();
          rowChangeTimer = null;
        }, 150);
      })
    );

    this._disposables.add(
      this.currentView$.subscribe(view => {
        if (!view) return;

        let columnsDebounceTimer: ReturnType<typeof setTimeout> | null = null;
        this._disposables.add(
          view.columnsWithoutFilter$.subscribe(_columns => {
            if (columnsDebounceTimer) clearTimeout(columnsDebounceTimer);
            columnsDebounceTimer = setTimeout(() => {
              this.setupCellTracking();
              this.scheduleReconcile();
              columnsDebounceTimer = null;
            }, 150);
          })
        );

      })
    );

    this._disposables.add(
      openActionItemInKanbanSlot.on(({ pageId, itemId }) => {
        try {
          const hostDocId = this.host?.doc?.id;
          if (!pageId || pageId !== hostDocId) {
            return;
          }
          if (!itemId) {
            return;
          }

          this.openDetailForRow(itemId);
        } catch (err) {
          console.warn('Failed to handle openActionItemInKanbanSlot:', err);
        }
      })
    );

    this.setupCellTracking();
    this.scheduleReconcile(500);
  }


  private getFormattedCellValue(cell: Cell, column: Column): string {
    try {
      // Use the proper human-readable string representation
      const columnType = column.type;

      // For most types, stringValue$ provides the best human-readable format
      const stringValue = cell.stringValue$?.value ?? '';

      // For certain types like select and multi-select, we may want to
      // use additional context from the column configuration
      if (columnType === 'select' || columnType === 'multi-select') {
        // For these types, we get a properly formatted string already
        return stringValue;
      } else if (columnType === 'checkbox') {
        // Convert boolean to 'True'/'False' for better readability
        const booleanValue = cell.value$?.value;
        return typeof booleanValue === 'boolean' ? (booleanValue ? 'True' : 'False') : 'False';
      } else if (columnType === 'progress') {
        // Format progress as percentage
        const progressValue = cell.value$?.value;
        return typeof progressValue === 'number' ? `${progressValue}%` : '0%';
      } else if (columnType === 'date') {
        // Date is already well-formatted by stringValue$
        return stringValue;
      } else if (columnType === 'rich-text') {
        // Prefer underlying Text's toString for rich-text columns
        const raw = cell.value$.value as unknown;
        if (
          typeof raw === 'object' &&
          raw !== null &&
          'toString' in (raw as object) &&
          typeof (raw as { toString: unknown }).toString === 'function'
        ) {
          try {
            return (raw as { toString: () => string }).toString();
          } catch {
            // noop
          }
        }
        return stringValue;
      } else if (columnType === 'relation') {
        // Handle relation type with better formatting
        const valueObj = cell.jsonValue$?.value;
        if (Array.isArray(valueObj)) {
          return `${valueObj.length} related items`;
        }
        return stringValue;
      } else {
        // Default to the string representation
        return stringValue;
      }
    } catch (e) {
      console.error('Error formatting cell value:', e);
      return String(cell.value$?.value ?? '');
    }
  }


  private readPlainText(cell: Cell): string {
    try {
      const stringValue = cell?.stringValue$?.value;
      if (typeof stringValue === 'string' && stringValue.length > 0) {
        return stringValue;
      }

      const raw = cell?.value$?.value as unknown;
      if (
        typeof raw === 'object' &&
        raw !== null &&
        'toString' in (raw as object) &&
        typeof (raw as { toString: unknown }).toString === 'function'
      ) {
        try {
          return (raw as { toString: () => string }).toString();
        } catch {
          return '';
        }
      }

      return typeof stringValue === 'string' ? stringValue : '';
    } catch {
      return '';
    }
  }

  private setupCellTracking() {
    if (this._isCleanedUp) return;
    if (this.setupInProgress) return;

    try {
      this.setupInProgress = true;

      if (!this.model?.isActionItemsDatabase) return;

      const currentView = this.currentView$.value;
      if (!currentView) return;

      if (!this.dataSource || !this.dataSource.rows$) {
        console.warn('DataSource or rows$ not available in setupCellTracking');
        return;
      }

      const rows = this.dataSource.rows$.value;
      // Include hidden columns so background sync covers all fields
      const columns = currentView.columnsWithoutFilter$.value;

      // Only track columns relevant to action items to reduce load
      const mapping = this.getColumnMapping(currentView, columns);
      const trackedColumnIds = [
        mapping.titleColId,
        mapping.statusColId,
        mapping.assigneeColId,
        mapping.dueDateColId,
        mapping.progressColId,
        mapping.priorityColId,
        mapping.contextColId,
        mapping.remindLaterColId,
      ].filter((v): v is string => Boolean(v));

      // Recreate subscriptions deterministically to avoid duplication
      this.clearCellSubscriptions();

      for (const rowId of rows) {
        for (const columnId of trackedColumnIds) {
          try {
            const column = currentView.columnGet(columnId);
            // Track all mapped columns, regardless of visibility in the view
            if (!column) continue;
            const cell = currentView.cellGet(rowId, columnId);
            if (!cell) continue;

            const cellKey = `${rowId}-${columnId}`;

            const handler = () => {
              if (this._isCleanedUp) return;

              const formattedValue = this.getFormattedCellValue(cell, column);

              if (!this.cellValueCache.has(cellKey)) {
                this.cellValueCache.set(cellKey, formattedValue);
                return;
              }

              if (this.cellValueCache.get(cellKey) === formattedValue) return;

              this.cellValueCache.set(cellKey, formattedValue);

              this.syncDatabaseChangeToProperties(
                rowId,
                columnId,
                column,
                cell,
                formattedValue
              );
            };

            const unsubscribers: Array<() => void> = [];
            // Subscribe to both raw and string value streams to catch all changes
            const unValue = cell.value$.subscribe(handler);
            if (typeof unValue === 'function') unsubscribers.push(unValue);
            const unString = cell.stringValue$.subscribe(handler);
            if (typeof unString === 'function') unsubscribers.push(unString);

            // Fallback: if neither stream supports subscribe, attempt one-time sync
            if (unsubscribers.length === 0) {
              handler();
            }

            if (unsubscribers.length > 0) {
              const unsubscribeAll = () => {
                for (const un of unsubscribers) {
                  try { un(); } catch { /* noop */ }
                }
              };
              this.cellSubscriptions.set(cellKey, unsubscribeAll);
            }
          } catch (e) {
            console.error(
              `[pique:database] Error tracking cell ${rowId}-${columnId}:`,
              e
            );
          }
        }
      }
    } finally {
      this.setupInProgress = false;
    }
  }

  override disconnectedCallback() {
    this._isCleanedUp = true;

    const pageId = this.host?.doc?.id;
    if (pageId) {
      try {
        const service = this.getActionItemsService();
        if (service) {
          service.unregisterDatabaseBlock(pageId);
        }
      } catch {}
    }

    this.clearCellSubscriptions();
    this.cellValueCache.clear();

    if (this.reconcileTimer) {
      clearTimeout(this.reconcileTimer);
      this.reconcileTimer = null;
    }

    super.disconnectedCallback();
  }


  private openDetailForRow(rowId: string, retry = 0) {
    try {
      const view = this.currentView$.value;
      if (!view || !rowId) {
        if (retry < 10) {
          setTimeout(() => this.openDetailForRow(rowId, retry + 1), 150);
        }
        return;
      }
      // Find the renderer element to call openDetailPanel
      const renderer = (this as unknown as Element).querySelector<HTMLElement & { openDetailPanel?: (args: { view: unknown; rowId: string }) => void }>(
        'affine-data-view-renderer'
      );
      if (renderer?.openDetailPanel) {
        renderer.openDetailPanel({ view, rowId });
        return;
      }
      // If renderer not yet ready, retry a few times
      if (retry < 10) {
        setTimeout(() => this.openDetailForRow(rowId, retry + 1), 200);
      }
    } catch (e) {
      console.warn('openDetailForRow failed:', e);
    }
  }

  private clearCellSubscriptions() {
    if (this.cellSubscriptions.size === 0) return;
    for (const dispose of this.cellSubscriptions.values()) {
      try {
        dispose();
      } catch {
        // ignore
      }
    }
    this.cellSubscriptions.clear();
  }

  private scheduleReconcile(delayMs: number = 250) {
    if (this._isCleanedUp) return;
    if (this.reconcileTimer) return;
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null;
      if (!this._isCleanedUp) {
        this.syncAllRowsToProperties();
      }
    }, delayMs);
  }

  // Removed legacy bulk sync setup; reconciliation is scheduled via subscriptions only

  private syncAllRowsToProperties() {
    if (this._isCleanedUp) return;

    try {
      if (!this.model?.isActionItemsDatabase) return;
      if (!this.host?.doc?.id) return;

      const currentView = this.currentView$.value;
      if (!currentView || !this.dataSource) return;

      // Get the workspace properties proxy
      const propertiesProxy = this.host.doc.collection.doc.getMapProxy<'affine:workspace-properties', WorkspaceAffineProperties>('affine:workspace-properties');
      if (!propertiesProxy.pageProperties) return;

      const pageId = this.host.doc.id;
      const pageProperties = propertiesProxy.pageProperties[pageId];
      if (!pageProperties?.system[PageSystemPropertyId.ActionItems]) return;

      const rows = readProxyArrayValue<string>(this.dataSource.rows$.value);
      const existingActionItems = readProxyArrayValue<SerializedTodoItem>(pageProperties.system[PageSystemPropertyId.ActionItems].value);
      const columns = readProxyArrayValue<string>(currentView.columnsWithoutFilter$.value);

      // Find column mappings
      const columnMapping = this.getColumnMapping(currentView, columns);

      // Load page attendees once to avoid repeated lookups
      let pageAttendees: SerializedAttendee[] = [];
      try {
        const propertiesProxy = this.host.doc.collection.doc.getMapProxy<'affine:workspace-properties', WorkspaceAffineProperties>('affine:workspace-properties');
        const pageProps = propertiesProxy.pageProperties?.[pageId];
        const attendeesValue = pageProps?.system?.attendees?.value as SerializedAttendee[] | undefined;
        if (Array.isArray(attendeesValue)) {
          pageAttendees = attendeesValue;
        }
      } catch { }

      const updatedActionItems: SerializedTodoItem[] = [];

      rows.forEach((rowId: string) => {
        try {
          const existing = existingActionItems.find(i => i.id === rowId);
          const base: SerializedTodoItem = existing ? existing : {
            id: rowId,
            title: '',
            pageId: pageId,
            pageTitle: this.host.doc?.meta?.title || 'Untitled',
            databaseId: this.model.id,
            columnId: columnMapping.titleColId || '',
            priority: ActionItemPrioritySchema.enum.Medium,
            createdAt: new Date(),
            context: '',
          };
          const actionItem: SerializedTodoItem = { ...base };

          if (columnMapping.titleColId) {
            const cell = currentView.cellGet(rowId, columnMapping.titleColId);
            if (cell && cell.value$) {
              const titleValue = cell.value$.value;
              if (titleValue) {
                const resolved = titleValue instanceof Text ? titleValue.toString() : String(titleValue);
                if (resolved.trim()) actionItem.title = resolved;
              }
            }
          }

          // Get status
          if (columnMapping.statusColId) {
            const cell = currentView.cellGet(rowId, columnMapping.statusColId);
            if (cell && cell.stringValue$) {
              const statusText = cell.stringValue$.value;
              if (statusText && ACTION_ITEM_STATUS_VALUES.includes(statusText as ActionItemStatus)) {
                actionItem.status = statusText as ActionItemStatus;
              }
            }
          }

          // Get assignee
          if (columnMapping.assigneeColId) {
            const assignees = this.getAssigneesFromRow(rowId, currentView, columnMapping.assigneeColId, pageAttendees);
            actionItem.assignedTo = dedupeAttendees(assignees || []);
          }

          // Get due date (BlockSuite stores as ms; convert to ISO string for properties layer)
          if (columnMapping.dueDateColId) {
            const cell = currentView.cellGet(rowId, columnMapping.dueDateColId);
            // Prefer raw numeric value if available
            const raw = cell?.value$?.value;
            if (typeof raw === 'number') {
              actionItem.dueDate = new Date(raw).toISOString();
            } else if (cell && cell.stringValue$) {
              const dueDateText = cell.stringValue$.value;
              if (dueDateText) {
                const parsed = Date.parse(dueDateText);
                actionItem.dueDate = isNaN(parsed) ? dueDateText : new Date(parsed).toISOString();
              }
            }
          }

          // Get progress
          if (columnMapping.progressColId) {
            const cell = currentView.cellGet(rowId, columnMapping.progressColId);
            if (cell && cell.value$) {
              const progressValue = cell.value$.value;
              if (typeof progressValue === 'number') {
                actionItem.progress = progressValue;
              }
            }
          }

          // Map Priority select value back into properties enum
          if (columnMapping.priorityColId) {
            const cell = currentView.cellGet(rowId, columnMapping.priorityColId);
            const label = cell?.stringValue$?.value;
            const parsed = ActionItemPrioritySchema.safeParse(label);
            if (parsed.success) {
              actionItem.priority = parsed.data;
            }
          }

          // Read Context rich-text into properties (plain text)
          if (columnMapping.contextColId) {
            const ctxCell = currentView.cellGet(rowId, columnMapping.contextColId);
            actionItem.context = this.readPlainText(ctxCell);
          }

          // Read Remind Later column
          if (columnMapping.remindLaterColId) {
            const cell = currentView.cellGet(rowId, columnMapping.remindLaterColId);
            const raw = cell?.value$?.value;
            if (Array.isArray(raw)) {
              actionItem.remindLater = raw;
            }
          }

          const rawModelSteps = typeof (this.model as any)?.getBreakdownSteps === 'function'
            ? (this.model as any).getBreakdownSteps(rowId)
            : undefined;
          let normalizedSteps = this.normalizeSteps(rawModelSteps as ActionPlanOption[] | ActionPlanOption | null | undefined);
          if (normalizedSteps.length === 0 && Array.isArray(existing?.breakdownSteps)) {
            normalizedSteps = this.normalizeSteps(existing.breakdownSteps as ActionPlanOption[]);
          }
          actionItem.breakdownSteps = normalizedSteps.map(step => ({
            id: step.id,
            title: step.title,
            description: step.description,
            completed: Boolean(step.completed),
          }));

          // Only add if we have a title
          if (actionItem.title?.trim()) {
            updatedActionItems.push(actionItem);
          }
        } catch (error) {
          console.warn(`Error processing row ${rowId}:`, error);
        }
      });

      const merged = mergeDatabaseRowActionItems({
        rows,
        rowItems: updatedActionItems,
        existingActionItems,
        databaseId: this.model.id,
      });
      if (merged.length > 0 || rows.length === 0) {
        reconcileActionItems(
          this.host.doc.collection.doc.getMap(AFFINE_PROPERTIES_ID),
          pageId,
          merged
        );
      }
    } catch (error) {
      console.error('[pique:database] Error syncing all rows to properties:', error);
    }
  }

  /**
   * Get column mapping for database sync
   */
  private getColumnMapping(currentView: { columnGet: (id: string) => Column }, columns: string[]) {
    const mapping: {
      titleColId?: string;
      statusColId?: string;
      assigneeColId?: string;
      dueDateColId?: string;
      progressColId?: string;
      priorityColId?: string;
      contextColId?: string;
      remindLaterColId?: string;
    } = {};

    for (const columnId of columns) {
      try {
        const column = currentView.columnGet(columnId);
        if (!column) continue;

        if (column.type === 'title' && !mapping.titleColId) {
          mapping.titleColId = columnId;
        } else if (column.type === 'select' && column.name === 'Status' && !mapping.statusColId) {
          mapping.statusColId = columnId;
        } else if ((column.name === "Who's Responsible?" || column.name === 'Assignee' || column.name === 'Assigned To' || column.name === 'Assign To') && !mapping.assigneeColId) {
          mapping.assigneeColId = columnId;
        } else if ((column.name === 'Due Date' || column.name === 'Deadline') && !mapping.dueDateColId) {
          mapping.dueDateColId = columnId;
        } else if ((column.name === 'Progress' || column.type === 'progress') && !mapping.progressColId) {
          mapping.progressColId = columnId;
        } else if (column.type === 'select' && column.name === 'Priority' && !mapping.priorityColId) {
          mapping.priorityColId = columnId; // Still map to derive label in DB UI
        } else if (column.name === 'Context' && !mapping.contextColId) {
          mapping.contextColId = columnId;
        } else if ((column.name === 'Remind Later' || column.name === 'remindLater') && !mapping.remindLaterColId) {
          mapping.remindLaterColId = columnId;
        }
      } catch (error) {
        console.warn(`Error accessing column ${columnId}:`, error);
      }
    }

    return mapping;
  }

  private syncDatabaseChangeToProperties(rowId: string, _columnId: string, column: Column, cell: Cell, formattedValue: string) {
    if (this._isCleanedUp) return;

    try {
      const blockModel = this.model;
      if (!blockModel || !this.host?.doc?.id) return;

      // Check if this database is marked as an action items database
      if (!blockModel.isActionItemsDatabase) return;

      // Get the workspace properties proxy
      const propertiesProxy = this.host.doc.collection.doc.getMapProxy<'affine:workspace-properties', WorkspaceAffineProperties>('affine:workspace-properties');
      if (!propertiesProxy.pageProperties) return;

      const pageId = this.host.doc.id;
      const pageProperties = propertiesProxy.pageProperties[pageId];
      if (!pageProperties?.system[PageSystemPropertyId.ActionItems]) return;

      const actionItems = pageProperties.system[PageSystemPropertyId.ActionItems].value as SerializedTodoItem[];

      // Find the action item that corresponds to this database row
      const actionItem = actionItems.find(item => item.id === rowId);
      if (!actionItem) return;

      // Update the action item based on the column that changed
      let updated = false;
      const updates: Partial<SerializedTodoItem> = {};

      if (column.type === 'title' || column.name === 'Title' || column.name === 'Task') {
        if (actionItem.title !== formattedValue) {
          updates.title = formattedValue;
          updated = true;
        }
      } else if (column.type === 'select' && (column.name === 'Status' || column.name === 'State') && actionItem.status !== formattedValue) {
        if (ACTION_ITEM_STATUS_VALUES.includes(formattedValue as ActionItemStatus)) {
          updates.status = formattedValue as ActionItemStatus;
          updated = true;

          // Update completion status based on status
          const lowerStatus = formattedValue.toLowerCase();
          if (lowerStatus.includes('done') || lowerStatus.includes('complete')) {
            updates.progress = 100;
          } else if (actionItem.progress === 100) {
            updates.progress = actionItem.progress && actionItem.progress < 100 ? actionItem.progress : 0;
          }
        }
      } else if (column.type === 'progress' || column.name === 'Progress') {
        const progressValue = cell.value$?.value;
        if (typeof progressValue === 'number' && actionItem.progress !== progressValue) {
          updates.progress = progressValue;
          updated = true;

          // Update status based on progress
          if (progressValue >= 100 && actionItem.status !== ACTION_ITEM_STATUS.DONE) {
            updates.status = ACTION_ITEM_STATUS.DONE;
          } else if (progressValue < 100 && actionItem.status === ACTION_ITEM_STATUS.DONE) {
            updates.status = ACTION_ITEM_STATUS.IN_PROGRESS;
          }
        }
      } else if (
        (column.name === "Who's Responsible?" || column.name === 'Assignee' || column.name === 'Assigned To') &&
        (() => {
          const view = this.currentView$.value;
          if (!view) return false;
          const cols = readProxyArrayValue<string>(view.columnsWithoutFilter$.value);
          const map = this.getColumnMapping(view, cols);
          return Boolean(map.assigneeColId);
        })()
      ) {
        const currentView = this.currentView$.value;
        if (!currentView) return;
        const columns = readProxyArrayValue<string>(currentView.columnsWithoutFilter$.value);
        const mapping = this.getColumnMapping(currentView, columns);
        const assignees = this.getAssigneesFromRow(rowId, currentView, mapping.assigneeColId as string);
        updates.assignedTo = dedupeAttendees(assignees || []);
        updated = true;
      } else if (column.type === 'select' && column.name === 'Priority') {
        const parsed = ActionItemPrioritySchema.safeParse(formattedValue);
        if (parsed.success) {
          updates.priority = parsed.data as ActionItemPriority;
          updated = true;
        }
      } else if ((column.name === 'Due Date' || column.name === 'Deadline') && actionItem.dueDate !== formattedValue) {
        updates.dueDate = formattedValue;
        updated = true;
      } else if (column.name === 'Context') {
        const text = this.readPlainText(cell);
        if (text && actionItem.context !== text) {
          updates.context = text;
          updated = true;
        }
      } else if (column.name === 'Remind Later' || column.name === 'remindLater') {
        const raw = cell.value$?.value;
        if (Array.isArray(raw)) {
          const currentRemind = actionItem.remindLater;
          const isSame = Array.isArray(currentRemind) &&
            currentRemind.length === raw.length &&
            currentRemind.every((v, i) => v === raw[i]);
          if (!isSame) {
            updates.remindLater = raw;
            updated = true;
          }
        }
      }

      if (updated && Object.keys(updates).length > 0) {
        updateActionItemFields(
          this.host.doc.collection.doc.getMap(AFFINE_PROPERTIES_ID),
          pageId,
          rowId,
          updates
        );
      }
    } catch (error) {
      console.error('[pique:database] Error syncing database change to properties:', error);
    }
  }

  /**
   * Add breakdown steps to a database row
   */
  public addBreakdownSteps(rowId: string, steps: ActionPlanOption[]): void {
    try {
      const normalized = this.normalizeSteps(steps);

      this.model.setBreakdownSteps(rowId, normalized);

      // Also update the breakdown steps column if it exists
      this.updateBreakdownStepsColumn(rowId, normalized);
    } catch (error) {
      console.error('Error adding breakdown steps:', error);
    }
  }

  /**
   * Update the breakdown steps column with formatted steps
   */
  private updateBreakdownStepsColumn(_rowId: string, _steps: ActionPlanOption[]): void {
    // No longer creating Action Plan Steps column - steps are stored in model only
    return;
  }

  /**
   * Get breakdown steps for a database row
   */
  public getBreakdownSteps(rowId: string): ActionPlanOption[] {
    try {
      return this.normalizeSteps(this.model.getBreakdownSteps(rowId) as ActionPlanOption[]);
    } catch (error) {
      console.error('Error getting breakdown steps:', error);
      return [];
    }
  }

  public handleBreakdownStepsUpdate(rowId: string, steps: ActionPlanOption[]): void {
    const normalized = this.normalizeSteps(steps);

    try {
      this.model.setBreakdownSteps(rowId, normalized);
    } catch (error) {
      console.error('[pique:database] Failed to write breakdown steps to model', error);
    }

    const actionItemsService = this.getActionItemsService();
    if (actionItemsService && rowId) {
      try {
        actionItemsService.updateBreakdownSteps(rowId, normalized, { skipDatabaseSync: true });
      } catch (error) {
        console.warn('[pique:database] Failed to send breakdown step update to ActionItemsService', error);
      }
    }

    this.scheduleReconcile(150);
  }

  /**
   * Override the renderBlock method to customize the detail panel configuration
   */
  override renderBlock() {
    const peekViewService = this.getRootService()?.peekViewService;

    const customDetailPanelConfig = {
      openDetailPanel: peekViewService
        ? (target: HTMLElement, template?: TemplateResult) => {
          // Extract the rowId directly from the generated template (affine-data-view-record-detail)
          const templateValues = (template as unknown as { values?: unknown[] })?.values;
          const extractedRowId =
            typeof templateValues?.[1] === 'string'
              ? (templateValues[1] as string)
              : undefined;

          const currentRowId = extractedRowId;
          const persistedSteps = currentRowId ? this.getBreakdownSteps(currentRowId) : [];
          this.setActionPlanOptions(persistedSteps);

          // Create a custom template that injects our components directly into the detail view
          const customNoteSlot = html`
            <div class="pique-action-plan-container">
              <pique-action-plan-panel
                .options=${persistedSteps}
                .host=${this.host}
                .context=${{
              taskTitle: this.getTitleForRow(currentRowId) || 'Action Item',
              taskDescription: this.getDescriptionForRow(currentRowId),
              transcriptionData: this.getTranscriptionData(),
              pageContent: this.getPageContent(),
              rowId: currentRowId,
              databaseComponent: this
            }}>
              </pique-action-plan-panel>
            </div>
            <div class="pique-action-container">
              <pique-action-panel
                .host="${this.host}"
                .view="${this.currentView$.value}">
              </pique-action-panel>
            </div>
          `;

          // Handler for "View All" button click
          const handleViewAllClick = () => {
            window.dispatchEvent(new CustomEvent('pique-navigate-to-action-items'));
          };

          // Inject View All button into modal controls after render
          const injectViewAllButton = () => {
            requestAnimationFrame(() => {
              const controlsContainer = document.querySelector('[class*="modalControls"]');
              const controlsRoot = controlsContainer?.querySelector('[class*="peek-view-controls"]');
              if (controlsRoot && !controlsRoot.querySelector('.pique-view-all-button')) {
                const btn = document.createElement('button');
                btn.className = 'pique-view-all-button';
                btn.title = 'View All Action Items';
                btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 6L21 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M11 12L21 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M11 18L21 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M3 7.39286C3 7.39286 4 8.04466 4.5 9C4.5 9 6 5.25 8 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3 18.3929C3 18.3929 4 19.0447 4.5 20C4.5 20 6 16.25 8 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`;
                btn.onclick = handleViewAllClick;
                controlsRoot.appendChild(btn);
              }
            });
          };
          setTimeout(injectViewAllButton, 100);

          // Create a wrapper with shareability at bottom as footer
          const wrappedTemplate = html`
            <div class="pique-detail-wrapper">
              <div class="pique-detail-original">
                ${template}
                ${customNoteSlot}
              </div>
              <div class="pique-shareability-footer">
                <pique-action-shareability
                  .rootService="${this.getRootService()}"
                  .isPrivate=${this.isPrivate}
                  .activeServices=${this.activeServices}
                  @privacy-changed=${this.handlePrivacyChanged}
                  @services-changed=${this.handleServicesChanged}
                ></pique-action-shareability>
              </div>
            </div>
            <style>
              .pique-view-all-button {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                padding: 0;
                border-radius: 50%;
                background-color: var(--affine-white, #fff);
                cursor: pointer;
                transition: background-color 0.15s ease;
                border: none;
                outline: none;
                box-shadow: var(--affine-shadow-1);
                color: var(--affine-icon-color);
              }
              .pique-view-all-button:hover {
                background-color: var(--affine-hover-color-filled, rgba(0,0,0,0.08));
                color: var(--affine-text-primary-color);
              }
              .pique-shareability-footer {
                position: sticky;
                bottom: 0;
                left: 0;
                right: 0;
                padding: 12px 16px;
                background: var(--affine-background-primary-color);
                border-top: 1px solid var(--affine-border-color, rgba(0,0,0,0.06));
                border-radius: 0 0 12px 12px;
                display: flex;
                justify-content: center;
                z-index: 10;
              }
              .pique-shareability-footer pique-action-shareability {
                margin: 0;
                filter: none;
              }
              .pique-action-plan-container {
                z-index: 1 !important;
                margin-bottom: 0;
              }
              .select-option-container {
                background: var(--affine-background-primary-color) !important;
              }
            </style>
          `;

          const styledTemplate = wrappedTemplate;

          return peekViewService.peek(target, styledTemplate);
        }
        : undefined,
      target: () => this.innerModalWidget.target,
    };

    return html`
      <div
        contenteditable="false"
        style="position: relative;background-color: var(--affine-background-primary-color);border-radius: 4px"
      >
        ${(this as any).dataView.render({
      bindHotkey: this._bindHotkey,
      handleEvent: this._handleEvent,
      selection$: this.viewSelection$,
      setSelection: this.setSelection,
      dataSource: this.dataSource,
      headerWidget: this.headerWidget,
      onDrag: this.onDrag,
      std: this.std,
      detailPanelConfig: customDetailPanelConfig,
    })}
      </div>
    `;
  }
}

export const PiqueDatabaseBlockSpec: BlockSpec = {
  schema: DatabaseBlockSchema,
  service: DatabaseBlockService,
  view: {
    component: literal`pique-database`,
  },
};
